import { FC } from 'react'
import { useSelector } from 'react-redux'
import { isDebtorType1 } from 'reducers/activecase/activecaseSlice'
import { useHistory } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  PLACE_COURSE_ORDER,
  PlaceCourseOrderVariables,
  PlaceCourseOrderResponse,
  PLACE_NEW_ATTORNEY_AND_CONSUMER_COURSE_ORDER,
  PlaceNewAttorneyAndConsumerCourseOrderVariables,
  PlaceNewAttorneyAndConsumerCourseOrderResponse
} from 'share/mutations/courses'
import GoBackHeader from 'components/organisms/GoBackHeader'
import {
  setCasePlaceOrderUpdateFields,
  setCaseForJointCaseOrderUpdateFields
} from 'reducers/activecase/activecaseSlice'
import {
  HasPlacedCourseOrderStatusType,
  CourseOrderStep,
  getCasePlaceOrderUpdateFields
} from 'share/domain/case'
import {
  clearSelectedCourse,
  getActiveTabCourseInvite,
  makeCourseActive,
  setOrderData
} from 'reducers/activecourse/activecourseSlice'
import {
  CourseInviteProvider,
  CourseMethodType,
  CoursePaymentType,
  CourseType,
  getCourseForJointCaseOrdersDescription
} from 'share/domain/course'
import {
  CourseCardContent,
  CourseCardHeader,
  CourseCardPrice,
  StyledCard,
  StyledCardFooter
} from 'components/atoms/StyledCard'
import FormattedPrice from 'components/atoms/FormattedPrice'
import { TwoBtnsContainer } from 'components/atoms/Layout'

import {
  CourseConfirmCardFooter,
  CourseConfirmCardWrapper,
  CourseConfirmContainer
} from './styles'

interface PlaceOrderResponse {
  success: boolean
  error: string
}

interface IHistory {
  type: 'phone' | 'online'
  coursePaymentType: CoursePaymentType
  courseProviderList: CourseInviteProvider[]
}

const CourseConfirm: FC = () => {
  const isDebtor1 = useSelector(isDebtorType1)
  const dispatch = useAppDispatch()
  const activeCase = useAppSelector((state) => state.activeCase)
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const courseInvite = useAppSelector(getActiveTabCourseInvite)
  const history = useHistory<IHistory>()
  const { coursePaymentType = null, type = 'online' } = history.location.state
  const [
    placeConsumerCourseOrder,
    { loading: placeConsumerCourseOrderLoading }
  ] =
    useMutation<PlaceCourseOrderResponse, PlaceCourseOrderVariables>(
      PLACE_COURSE_ORDER
    )

  const [
    placeAttorneyAndConsumerCourseOrder,
    { loading: placeAttorneyAndConsumerCourseOrderLoading }
  ] = useMutation<
    PlaceNewAttorneyAndConsumerCourseOrderResponse,
    PlaceNewAttorneyAndConsumerCourseOrderVariables
  >(PLACE_NEW_ATTORNEY_AND_CONSUMER_COURSE_ORDER)

  const loadings =
    placeConsumerCourseOrderLoading ||
    placeAttorneyAndConsumerCourseOrderLoading ||
    !activeCase.matter ||
    activeCourse.isLoadingCourse

  const loadingDelay = useLoadingDelay(loadings)

  if (!activeCourse.selectedCourse) {
    return <div>Error no course selected</div>
  }

  function getFirstCourseForJointCaseOrders(): number | null | undefined {
    if (isDebtor1) {
      return activeCase.matter?.debtor1FirstCourseForJointCaseOrders
    } else {
      return activeCase.matter?.debtor2FirstCourseForJointCaseOrders
    }
  }

  function getSecondCourseForJointCaseOrders(): number | null | undefined {
    if (isDebtor1) {
      return activeCase.matter?.debtor1SecondCourseForJointCaseOrders
    } else {
      return activeCase.matter?.debtor2SecondCourseForJointCaseOrders
    }
  }

  function getHasPlacedFirstCourseOrder(): boolean | undefined {
    if (isDebtor1) {
      return activeCase.matter?.debtor1HasPlacedFirstCourseOrder
    } else {
      return activeCase.matter?.debtor2HasPlacedFirstCourseOrder
    }
  }

  function getHasPlacedSecondCourseOrder(): boolean | undefined {
    if (isDebtor1) {
      return activeCase.matter?.debtor1HasPlacedSecondCourseOrder
    } else {
      return activeCase.matter?.debtor2HasPlacedSecondCourseOrder
    }
  }

  function isJointOrder(): boolean {
    if (activeCase) {
      if (activeCase.matter) {
        if (activeCase.matter.isJoint) {
          return activeCase.matter.isJoint
        }
      }
    }
    return false
  }

  function getJointDescription(): string {
    if (activeCase) {
      if (activeCase.matter) {
        if (activeCase.matter.isJoint) {
          if (activeCourse.activeTab === 'first') {
            return getCourseForJointCaseOrdersDescription(
              getFirstCourseForJointCaseOrders()
            )
          } else {
            return getCourseForJointCaseOrdersDescription(
              getSecondCourseForJointCaseOrders()
            )
          }
        }
      }
    }
    return ''
  }

  function hasPlacedCourseOrder(): boolean {
    if (activeCourse.activeTab === 'first') {
      return getHasPlacedFirstCourseOrder() ?? false
    } else {
      return getHasPlacedSecondCourseOrder() ?? false
    }
  }

  async function placeAttorneyAndConsumerOrder(
    orderVariables: PlaceNewAttorneyAndConsumerCourseOrderVariables
  ): Promise<PlaceOrderResponse> {
    const result: PlaceOrderResponse = {
      success: false,
      error: ''
    }

    const errorCasePlaceOrderUpdateFields = getCasePlaceOrderUpdateFields(
      isDebtor1,
      activeCourse?.activeTab === 'first',
      HasPlacedCourseOrderStatusType.ErrorOnPlaceAttorneyConsumerOrderStep,
      CourseOrderStep.CourseOrderFailed
    )

    try {
      const placeOrderResult = await placeAttorneyAndConsumerCourseOrder({
        variables: orderVariables
      })
      const placeOrderResultData =
        placeOrderResult.data?.orders.placeNewAttorneyAndConsumerCourseOrder
      if (placeOrderResultData?.success) {
        result.success = true
        result.error = ''
        dispatch(setOrderData(placeOrderResultData.orders))
        dispatch(makeCourseActive())
        dispatch(
          setCasePlaceOrderUpdateFields(
            placeOrderResultData.updateCasePlaceOrderFields
          )
        )
        const forJointData = {
          isDebtor1: isDebtor1,
          firstCourseForJointCaseOrders:
            placeOrderResultData?.firstCourseForJointCaseOrders ?? null,
          secondCourseForJointCaseOrders:
            placeOrderResultData?.secondCourseForJointCaseOrders ?? null
        }
        dispatch(setCaseForJointCaseOrderUpdateFields(forJointData))
      } else {
        result.error =
          placeOrderResultData?.errorMessage ?? 'Unknown error placing order'
        console.log('error 1')
        console.dir(errorCasePlaceOrderUpdateFields)
        dispatch(setCasePlaceOrderUpdateFields(errorCasePlaceOrderUpdateFields))
      }
    } catch (e) {
      result.success = false
      result.error = 'Unknown error placing order'
      console.log('error placing order')
      console.log('error 2')
      console.dir(errorCasePlaceOrderUpdateFields)
      dispatch(setCasePlaceOrderUpdateFields(errorCasePlaceOrderUpdateFields))
    }
    return result
  }

  async function placeConsumerOrderOnly(
    orderVariables: PlaceCourseOrderVariables
  ): Promise<PlaceOrderResponse> {
    const result: PlaceOrderResponse = {
      success: false,
      error: ''
    }
    try {
      const placeOrderResult = await placeConsumerCourseOrder({
        variables: orderVariables
      })
      if (placeOrderResult.data?.orders.placeCourseOrder) {
        result.success = true
        result.error = ''
      } else {
        console.log('error placing order')
        result.success = false
        result.error = 'Unknown error placing order'
      }
    } catch (e) {
      result.success = false
      result.error = 'Unknown error placing order'
      console.log('error placing order')
    }
    return result
  }

  async function orderCourseClick() {
    let result: PlaceOrderResponse = {
      success: false,
      error: ''
    }
    if (hasPlacedCourseOrder()) {
      const placeOrderInput = {
        placeCourseOrder: {
          caseId: activeCase.matter?.id ? activeCase.matter?.id : 0,
          orderId: courseInvite ? courseInvite.orderId : 0,
          orderItemId: activeCourse.selectedCourse
            ? activeCourse.selectedCourse.orderItemId
            : null,
          providerId: activeCourse.selectedCourse?.productKey
            ? activeCourse.selectedCourse?.productKey
            : 0,
          courseMethodType:
            type === 'phone' ? CourseMethodType.Phone : CourseMethodType.Online
        }
      }

      result = await placeConsumerOrderOnly(placeOrderInput)
    } else {
      const placeOrderInput = {
        request: {
          caseId: activeCase.matter?.id ? activeCase.matter?.id : 0,
          courseType:
            activeCourse?.activeTab === 'first'
              ? CourseType.CreditCounseling
              : CourseType.DebtorEducation,
          providerId: activeCourse.selectedCourse?.productKey
            ? activeCourse.selectedCourse?.productKey
            : 0,
          courseMethodType:
            type === 'phone' ? CourseMethodType.Phone : CourseMethodType.Online,
          isDebtor1: isDebtor1,
          isJoint: isJointOrder()
        }
      }

      result = await placeAttorneyAndConsumerOrder(placeOrderInput)
    }

    if (result.success) {
      dispatch(clearSelectedCourse())
      history.push('/dashboard/courses')
    } else {
      console.log('error occurred placing order')
    }
  }

  function cancelClick() {
    dispatch(clearSelectedCourse())
    history.push('/dashboard/courses')
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <GoBackHeader text='Courses' />
      <CourseConfirmContainer data-testid='course-confirm-page'>
        <h2 className='mb-4' data-testid='course-confirm-main-header'>
          Confirm Course
        </h2>
        <h3 className='mb-4' data-testid='course-confirm-secondary-header'>
          You selected the following course:
        </h3>
        <CourseConfirmCardWrapper data-testid='course-confirm-card-wrapper'>
          <StyledCard>
            <Card.Body>
              <CourseCardHeader>
                <h4 data-testid='course-confirm-selected-course-name'>
                  {activeCourse.selectedCourse.name}
                </h4>
                <CourseCardPrice>
                  <p>Course Price</p>
                  {coursePaymentType === CoursePaymentType.ClientPays && (
                    <span data-testid='course-confirm-clientpays-price'>
                      <FormattedPrice
                        price={activeCourse.selectedCourse.price}
                      />
                    </span>
                  )}
                  {coursePaymentType === CoursePaymentType.FirmPays && (
                    <span
                      className='long'
                      data-testid='course-confirm-firmpays-price'
                    >
                      Billed to Law Firm
                    </span>
                  )}
                </CourseCardPrice>
              </CourseCardHeader>
              {activeCourse.selectedCourse.valueProps && (
                <CourseCardContent
                  data-testid='course-confirm-value-props'
                  dangerouslySetInnerHTML={{
                    __html: activeCourse.selectedCourse.valueProps.toString()
                  }}
                />
              )}
            </Card.Body>
            <StyledCardFooter data-testid='course-confirm-footer'>
              {type === 'online' && (
                <CourseConfirmCardFooter data-testid='course-confirm-online-info'>
                  <span className='mdi mdi-monitor' />
                  <span data-testid='course-confirm-online-info-title'>
                    Taking course online
                  </span>
                </CourseConfirmCardFooter>
              )}
              {type === 'phone' && (
                <CourseConfirmCardFooter data-testid='course-confirm-phone-info'>
                  <span className='mdi mdi-phone' />
                  <span data-testid='course-confirm-phone-info-title'>
                    Taking course by phone call
                  </span>
                </CourseConfirmCardFooter>
              )}
            </StyledCardFooter>
            {isJointOrder() && getJointDescription() && (
              <StyledCardFooter data-testid='course-confirm-joint-footer'>
                <CourseConfirmCardFooter>
                  <span className='mdi mdi-account-multiple' />
                  <span data-testid='course-confirm-joint-footer-description'>
                    {getJointDescription()}
                  </span>
                </CourseConfirmCardFooter>
              </StyledCardFooter>
            )}
          </StyledCard>
        </CourseConfirmCardWrapper>
        <h3 className='mb-3' data-testid='course-confirm-are-you-sure'>
          Are you sure you want to take this course?
        </h3>
        <p className='mb-4' data-testid='course-confirm-warning-message'>
          After you start this course, you cannot change to a different course.
        </p>
        <TwoBtnsContainer>
          <Button
            variant='outline-secondary'
            onClick={cancelClick}
            data-testid='course-confirm-cancel-button'
          >
            Cancel
          </Button>
          <Button
            onClick={orderCourseClick}
            data-testid='course-confirm-take-course-button'
          >
            Take This Course
          </Button>
        </TwoBtnsContainer>
      </CourseConfirmContainer>
    </>
  )
}

export default CourseConfirm
